/**
 * Diagnosis dimension library — pre-built dimensions organized by use-case category.
 *
 * NFR-003: Code constants, NOT database. Mirrors backend diagnosis_library.py exactly.
 *
 * Organization: by use-case category (Brand Building, Direct Response, Production,
 * Universal, Image-Specific).
 */

import type { DiagnosisDimension } from '@/types/template';

// ---------------------------------------------------------------------------
// Category metadata
// ---------------------------------------------------------------------------

export interface DiagnosisCategory {
  key: string;
  name: string;
  description: string;
}

export const DIAGNOSIS_CATEGORIES: DiagnosisCategory[] = [
  {
    key: 'brand_building',
    name: 'Brand Building',
    description:
      'Dimensions for evaluating long-term brand equity, mental availability, ' +
      'and emotional resonance. Based on Ehrenberg-Bass, System1, and Binet & Field frameworks.',
  },
  {
    key: 'direct_response',
    name: 'Direct Response',
    description:
      'Dimensions for evaluating conversion-oriented creatives: hooks, offers, ' +
      'urgency, and social proof. Based on AIDA, Cialdini, and CRO frameworks.',
  },
  {
    key: 'production',
    name: 'Production',
    description:
      'Dimensions for evaluating technical production quality: audio clarity, ' +
      'visual coherence, and polish level.',
  },
  {
    key: 'universal',
    name: 'Universal',
    description:
      'Dimensions applicable to any template type: message retention, ' +
      'target alignment, and comprehension.',
  },
  {
    key: 'image_specific',
    name: 'Image-Specific',
    description:
      'Dimensions specifically designed for static image creatives: visual hierarchy, ' +
      'composition, and layout analysis.',
  },
];

// ---------------------------------------------------------------------------
// Brand Building dimensions
// ---------------------------------------------------------------------------

const BRAND_BUILDING_DIMENSIONS: DiagnosisDimension[] = [
  {
    dimensionId: 'dim_cepMentalAvailability',
    name: 'CEP & Mental Availability',
    instructions:
      'Analyze which category entry points (buying occasions, needs, motivations) ' +
      'this ad links to the brand. Look for scenes, situations, emotions, and triggers ' +
      'that represent moments when someone would choose this category. Rate the overall ' +
      'level of CEP activation and list each CEP activated with the cue that triggers it. ' +
      'Assess whether the ad builds broad mental availability or narrow niche association.',
    videoInstructions:
      'For video: track how CEPs are introduced over time. Note whether buying occasions ' +
      'are shown visually (scenes depicting usage moments), verbally (voiceover mentioning ' +
      'occasions), or both. Assess whether multiple CEPs build breadth or dilute focus.',
    imageInstructions:
      'For image: identify the single buying occasion or need state depicted. Assess ' +
      'whether the situation is immediately recognizable and whether the brand-occasion ' +
      'link is clear without requiring explanation.',
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_brandLinkage',
    name: 'Brand Linkage',
    instructions:
      'Assess how strongly the creative content connects to the brand — how easily ' +
      'would a viewer attribute this ad to the correct brand? Look for distinctive ' +
      'brand assets (colors, characters, sonic logos, taglines), brand name frequency, ' +
      'and whether the brand is central vs. incidental to the story. Identify which ' +
      "assets drive linkage and note any 'borrowed interest' risk where the content " +
      'overshadows the brand.',
    videoInstructions:
      'For video: pay special attention to brand appearance timing, frequency of brand ' +
      'mentions, and whether the brand is woven into the narrative or bolted on at the end. ' +
      'Note sonic branding and how early the brand is identifiable.',
    imageInstructions:
      'For image: assess logo placement and size, brand color dominance in the composition, ' +
      'and whether the brand identity is immediately recognizable or requires searching. ' +
      'Consider whether removing the logo would make the brand unidentifiable.',
    outputConfig: { type: 'options', options: ['Low', 'Medium', 'High'] },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_emotionalRegister',
    name: 'Emotional Register',
    instructions:
      'Determine the dominant emotional and persuasion mode — is this ad building ' +
      'long-term brand feeling or driving immediate action? Look for storytelling depth, ' +
      'emotional arcs, and character development (brand-building signals) vs. urgency, ' +
      'offers, product features, and CTAs (activation signals). Describe the emotional ' +
      'arc, identify the primary emotion elicited, and explain how this register serves ' +
      'or undermines the campaign objective.',
    videoInstructions:
      'For video: track emotional arc over time, noting shifts in tone, music cues, ' +
      'and pacing changes that signal emotional register transitions. Identify whether ' +
      'the emotional journey builds to a brand moment or a conversion ask.',
    imageInstructions:
      'For image: assess the overall mood, color temperature, facial expressions, ' +
      'and whether the single frame communicates emotion through composition rather ' +
      'than narrative. Note if the emotional tone aligns with brand-building or activation.',
    outputConfig: {
      type: 'options',
      options: ['brand_building', 'hybrid', 'activation'],
    },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_narrativeStructure',
    name: 'Narrative Structure',
    instructions:
      'Analyze the structural form of the narrative — how the creative builds, holds, ' +
      'and resolves viewer attention over time. Look for open loops (unresolved questions ' +
      'that pull the viewer forward), payoff moments, pacing variation, and tension ' +
      'mechanics. Map the structural beats, identify where retention risk is highest, ' +
      'and assess whether the structure fits the video length and placement.',
    mediaTypes: ['video'],
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_distinctiveness',
    name: 'Distinctiveness',
    instructions:
      'Evaluate brand recognizability — could a viewer identify this brand without ' +
      'seeing the logo? Look for distinctive brand assets: unique color palettes, ' +
      'characters, visual style, sonic identity, typography, and recurring creative ' +
      'elements that differentiate from competitors. Assess whether the creative ' +
      "reinforces or dilutes the brand's distinctive memory structures.",
    videoInstructions:
      "For video: consider sonic branding, character distinctiveness, editing style, " +
      "and visual motifs that appear consistently across the brand's creative portfolio.",
    imageInstructions:
      'For image: focus on visual identity elements — color palette, typography, ' +
      'illustration style, photography treatment — that make the brand instantly ' +
      'recognizable in a feed without reading the logo.',
    outputConfig: {
      type: 'options',
      options: ['generic', 'partial', 'distinctive', 'iconic'],
    },
    enabled: true,
    isSystem: false,
  },
];

// ---------------------------------------------------------------------------
// Direct Response dimensions
// ---------------------------------------------------------------------------

const DIRECT_RESPONSE_DIMENSIONS: DiagnosisDimension[] = [
  {
    dimensionId: 'dim_hookEffectiveness',
    name: 'Hook Effectiveness',
    instructions:
      'Assess the scroll-stopping power of the opening. Look for pattern interrupts, ' +
      'bold visual or textual hooks, unexpected elements, and whether the first 1-3 ' +
      'seconds create enough curiosity or urgency to prevent scrolling. Rate based on ' +
      'the strength of the attention capture mechanism and its relevance to the target ' +
      'audience. Identify the specific hook mechanism used (visual shock, question, ' +
      'bold claim, relatable pain point, etc.).',
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_offerClarity',
    name: 'Offer Clarity',
    instructions:
      'Evaluate how clearly the value proposition and offer are communicated. ' +
      "Check whether the offer is specific (not vague like 'save big'), quantified " +
      'where possible (exact discount, price point), presented early enough to retain ' +
      'attention, and differentiated from competitors. A clear offer should be immediately ' +
      'understandable by a distracted mobile viewer on first exposure.',
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_activationUrgency',
    name: 'Activation Urgency',
    instructions:
      'Assess the presence and strength of urgency and scarcity triggers. Look for ' +
      'time-limited offers, countdown elements, low-stock signals, exclusive access ' +
      'messaging, and deadline language. Evaluate whether the urgency feels credible ' +
      'and appropriate for the offer, or manufactured and likely to erode trust. ' +
      'Note: absence of urgency is not inherently negative — evaluate in context of ' +
      'the campaign objective.',
    outputConfig: { type: 'binary' },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_socialProof',
    name: 'Social Proof',
    instructions:
      'Evaluate the use of testimonials, reviews, user counts, trust signals, and ' +
      "social validation elements. Look for customer quotes, star ratings, user count " +
      "claims ('10,000+ customers'), press logos, certifications, before/after evidence, " +
      'and money-back guarantees. Assess the credibility and prominence of each social ' +
      'proof element present and whether trust-building is sufficient for the conversion ' +
      'ask being made.',
    outputConfig: { type: 'binary' },
    enabled: true,
    isSystem: false,
  },
];

// ---------------------------------------------------------------------------
// Production dimensions
// ---------------------------------------------------------------------------

const PRODUCTION_DIMENSIONS: DiagnosisDimension[] = [
  {
    dimensionId: 'dim_audioClarity',
    name: 'Audio Clarity',
    instructions:
      'Evaluate audio quality, mixing balance, and voice intelligibility. Check for ' +
      'distortion, clipping, uneven levels, background noise interference, and whether ' +
      'speech (if present) is fully clear. Assess music-to-voice balance and whether ' +
      'audio enhances or detracts from the message. Consider whether the audio works ' +
      'at typical mobile listening volumes.',
    mediaTypes: ['video'],
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_visualCoherence',
    name: 'Visual Coherence',
    instructions:
      'Evaluate color grading consistency, lighting quality, and visual style coherence ' +
      'throughout the creative. Check for unintended color shifts between shots, lighting ' +
      'mismatches, inconsistent visual treatment, and whether the overall visual style ' +
      'feels intentional and polished. A coherent creative should feel like one unified ' +
      'piece, not a collection of disparate shots.',
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
];

// ---------------------------------------------------------------------------
// Universal dimensions
// ---------------------------------------------------------------------------

const UNIVERSAL_DIMENSIONS: DiagnosisDimension[] = [
  {
    dimensionId: 'dim_messageRetention',
    name: 'Message Retention',
    instructions:
      'Evaluate the likelihood that the key message will be remembered after a single ' +
      'exposure. Consider message simplicity, repetition within the creative, ' +
      'distinctiveness of framing, emotional anchoring, and whether the message is ' +
      'concrete enough to be retold. A retained message should be expressible in one ' +
      'sentence by a viewer who saw the ad once.',
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_targetAlignment',
    name: 'Target Alignment',
    instructions:
      'Evaluate how well the creative speaks to the intended audience. Consider language ' +
      'register, visual representation, cultural cues, pain points addressed, and whether ' +
      "the creative would resonate with the target demographic or feel generic. A well-aligned " +
      "creative makes the viewer feel 'this is for me' within seconds. Note any elements " +
      'that may alienate the target audience.',
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
];

// ---------------------------------------------------------------------------
// Image-Specific dimensions
// ---------------------------------------------------------------------------

const IMAGE_SPECIFIC_DIMENSIONS: DiagnosisDimension[] = [
  {
    dimensionId: 'dim_visualHierarchy',
    name: 'Visual Hierarchy',
    instructions:
      "Evaluate eye flow, focal points, and information hierarchy in the image. " +
      "Assess whether the viewer's eye is guided naturally from the most important " +
      'element to supporting information. Check for clear primary focal point, logical ' +
      'reading order, and whether key information (headline, product, CTA) receives ' +
      'appropriate visual weight. Consider how the hierarchy works at typical mobile ' +
      'display sizes.',
    mediaTypes: ['image'],
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
  {
    dimensionId: 'dim_compositionLayout',
    name: 'Composition & Layout',
    instructions:
      'Evaluate balance, whitespace usage, and visual weight distribution in the image. ' +
      'Assess whether elements are well-distributed, the layout feels intentional, and ' +
      'whitespace is used effectively to create breathing room and guide attention. Check ' +
      'for cramped areas, visual clutter, and overall compositional harmony. Consider ' +
      'rule of thirds, golden ratio, and whether the composition works for the intended ' +
      'placement format.',
    mediaTypes: ['image'],
    outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
    enabled: true,
    isSystem: false,
  },
];

// ---------------------------------------------------------------------------
// Library organized by category
// ---------------------------------------------------------------------------

export const DIAGNOSIS_LIBRARY: Record<string, DiagnosisDimension[]> = {
  brand_building: BRAND_BUILDING_DIMENSIONS,
  direct_response: DIRECT_RESPONSE_DIMENSIONS,
  production: PRODUCTION_DIMENSIONS,
  universal: UNIVERSAL_DIMENSIONS,
  image_specific: IMAGE_SPECIFIC_DIMENSIONS,
};

// ---------------------------------------------------------------------------
// Flat lookup by dimension ID
// ---------------------------------------------------------------------------

export const ALL_DIMENSIONS: Record<string, DiagnosisDimension> = Object.values(DIAGNOSIS_LIBRARY)
  .flat()
  .reduce<Record<string, DiagnosisDimension>>((acc, dim) => {
    acc[dim.dimensionId] = dim;
    return acc;
  }, {});
